import { useSyncExternalStore } from "react"

import { createSyncedPref } from "@/shared/lib/storage/storage"
import type { LanguageCode } from "@/shared/types/language.types"

import { isSupportedLanguage } from "../../lib/type-guards"

const CLOUD_STORAGE_KEY = "user_language"
const LEGACY_LOCAL_KEY = "celestia_user_language"

// One-time migration from the pre-2026 plain-string localStorage key.
// Without this, users who already picked a language get re-prompted because
// createSyncedPref only reads CLOUD_STORAGE_KEY (JSON-encoded).
const migrateLegacyLocalKey = () => {
  try {
    const legacy = localStorage.getItem(LEGACY_LOCAL_KEY)
    if (!legacy || !isSupportedLanguage(legacy)) return
    if (localStorage.getItem(CLOUD_STORAGE_KEY) !== null) {
      localStorage.removeItem(LEGACY_LOCAL_KEY)
      return
    }
    localStorage.setItem(CLOUD_STORAGE_KEY, JSON.stringify(legacy))
    localStorage.removeItem(LEGACY_LOCAL_KEY)
  } catch {
    // localStorage may be unavailable (SSR / privacy mode) — fail silently
  }
}

migrateLegacyLocalKey()

const languagePrefStore = createSyncedPref<LanguageCode>(CLOUD_STORAGE_KEY, {
  validate: isSupportedLanguage
})

type LanguagePreferenceState = {
  language: LanguageCode | null
  isReady: boolean
  setLanguage: (language: LanguageCode) => Promise<void>
  clearLanguage: () => Promise<void>
}

export const useLanguagePreference = (): LanguagePreferenceState => {
  const language = useSyncExternalStore(
    listener => languagePrefStore.subscribe(listener),
    () => languagePrefStore.getSnapshot(),
    () => languagePrefStore.getSnapshot()
  )
  const isReady = useSyncExternalStore(
    listener => languagePrefStore.subscribeBootstrap(listener),
    () => languagePrefStore.getBootstrapSnapshot(),
    () => languagePrefStore.getBootstrapSnapshot()
  )

  return {
    language,
    isReady,
    setLanguage: language => languagePrefStore.set(language),
    clearLanguage: () => languagePrefStore.clear()
  }
}
